import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, Textarea, InputLabel, InputGroup } from '../../../components/ui';
import { cn } from '../../../lib/cn';

export default function LessonsNew({ course, unit }) {
  const { t } = useTranslation();

  const LESSON_TYPES = [
    { id: 'reading_lesson', label: t('teacherLessons.typeReading') },
    { id: 'writing_workshop', label: t('teacherLessons.typeWriting') },
    { id: 'vocabulary', label: t('teacherLessons.typeVocabulary') },
    { id: 'grammar', label: t('teacherLessons.typeGrammar') },
    { id: 'mixed', label: t('teacherLessons.typeMixed') },
  ];
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    lesson_type: 'mixed',
    scheduled_for: '',
    duration_minutes: '',
    location: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.post(`/teacher/courses/${course.id}/units/${unit.id}/lessons`, { lesson: formData }, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  const computedEndTime = () => {
    if (!formData.scheduled_for) return null;
    const start = new Date(formData.scheduled_for);
    if (isNaN(start.getTime())) return null;
    const minutes = parseInt(formData.duration_minutes, 10) || 60;
    const end = new Date(start.getTime() + minutes * 60000);
    return end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <TeacherShell scrollTitle={<><Link href={`/teacher/courses/${course.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{course.title}</Link>{`  ›  ${t('teacherLessons.newLesson')}`}</>}>
      <TeacherPageHeader
        title={t('teacherLessons.newLesson')}
        subtitle={`in ${unit.title} — ${course.title}`}
        backLink={`/teacher/courses/${course.id}`}
        backLabel={`${t('teacherLessons.backTo')} ${course.title}`}
      />

      <div className="max-w-2xl">
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <InputGroup>
              <InputLabel htmlFor="title" required>{t('teacherLessons.titleField')}</InputLabel>
              <Input
                id="title"
                value={formData.title}
                onChange={handleChange('title')}
                placeholder={t('teacherLessons.titlePlaceholder')}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="description">{t('teacherLessons.description')}</InputLabel>
              <Textarea
                id="description"
                value={formData.description}
                onChange={handleChange('description')}
                rows={3}
                placeholder={t('teacherLessons.descriptionPlaceholder')}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>{t('teacherLessons.lessonType')}</InputLabel>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {LESSON_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, lesson_type: type.id }))}
                    className={cn(
                      'p-3 rounded-lg border-2 text-center text-sm font-medium transition-all',
                      formData.lesson_type === type.id
                        ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800 text-gray-900 dark:text-white'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </InputGroup>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputGroup>
                <InputLabel htmlFor="scheduled_for">{t('teacherLessons.scheduledDateTime')}</InputLabel>
                <Input
                  id="scheduled_for"
                  type="datetime-local"
                  value={formData.scheduled_for}
                  onChange={handleChange('scheduled_for')}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel htmlFor="duration_minutes">{t('teacherLessons.durationMinutes')}</InputLabel>
                <Input
                  id="duration_minutes"
                  type="number"
                  min="1"
                  value={formData.duration_minutes}
                  onChange={handleChange('duration_minutes')}
                  placeholder="60"
                />
                {computedEndTime() && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('teacherLessons.endsAt')} {computedEndTime()}
                  </p>
                )}
              </InputGroup>
            </div>

            <InputGroup>
              <InputLabel htmlFor="location">{t('teacherLessons.location')}</InputLabel>
              <Input
                id="location"
                value={formData.location}
                onChange={handleChange('location')}
                placeholder={t('teacherLessons.locationPlaceholder')}
              />
            </InputGroup>

            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="submit" variant="primary" disabled={isSubmitting || !formData.title.trim()}>
                {isSubmitting ? t('teacherLessons.creating') : t('teacherLessons.createLesson')}
              </Button>
              <Link href={`/teacher/courses/${course.id}`}>
                <Button type="button" variant="ghost">{t('common.cancel')}</Button>
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </TeacherShell>
  );
}
